import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  ChequeIssuanceDto,
  ChequeIssuanceListDto,
  ChequeIssuanceFilterDto,
  CreateChequeIssuanceRequest,
  AcknowledgeChequeIssuanceRequest,
  MarkCollectedRequest,
  CancelChequeIssuanceRequest,
} from '@/types/chequeIssuance.types';

/** A cheque we have written that nobody has collected yet. */
export interface ChequeAwaitingIssuance {
  chequeRecordId:    string;
  chequeNumber:      string;
  chequeDate:        string;
  bankAccountCode:   string;
  bankAccountName:   string;
  payeeName:         string;
  amount:            number;
  currencyCode:      string;
  referenceDocument: string | null;
  /** 0 = written but never printed. Anything above 0 is physically on paper. */
  printCount:        number;
}

export const chequeIssuanceApi = {
  /** GET /api/v1/cheque-issuances — paginated list with optional filter */
  getList: (filter?: ChequeIssuanceFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<ChequeIssuanceListDto>>>('/cheque-issuances', {
      params: filter,
    }),

  /**
   * GET /api/v1/cheque-issuances/awaiting — cheques written but not yet handed over.
   *
   * The hand-over queue. Eligibility matches what creating an issuance enforces, so nothing listed
   * here can be refused on save.
   */
  getAwaiting: (includeUnprinted = true) =>
    apiClient.get<ApiResponse<ChequeAwaitingIssuance[]>>('/cheque-issuances/awaiting',
      { params: { includeUnprinted } }),

  /** GET /api/v1/cheque-issuances/{id} — full issuance record */
  getById: (id: string) =>
    apiClient.get<ApiResponse<ChequeIssuanceDto>>(`/cheque-issuances/${id}`),

  /** POST /api/v1/cheque-issuances — create a new issuance */
  create: (data: CreateChequeIssuanceRequest) =>
    apiClient.post<ApiResponse<ChequeIssuanceDto>>('/cheque-issuances', data),

  /** POST /api/v1/cheque-issuances/{id}/acknowledge */
  acknowledge: (id: string, data: AcknowledgeChequeIssuanceRequest) =>
    apiClient.post<ApiResponse<ChequeIssuanceDto>>(`/cheque-issuances/${id}/acknowledge`, data),

  /**
   * POST /api/v1/cheque-issuances/{id}/collect — the payee has taken the cheque away.
   *
   * The route is `collect`, not `mark-collected`: this client called the latter and every attempt
   * 404'd, which the screen reported as "Failed to mark collected".
   *
   * Returns no body — the caller reloads the record to see the new status.
   */
  markCollected: (id: string, data: MarkCollectedRequest) =>
    apiClient.post<ApiResponse<void>>(`/cheque-issuances/${id}/collect`, data),

  /** POST /api/v1/cheque-issuances/{id}/cancel */
  cancel: (id: string, data: CancelChequeIssuanceRequest) =>
    apiClient.post<ApiResponse<void>>(`/cheque-issuances/${id}/cancel`, data),
};
